import { useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { useEDITransactionCodes } from '~/hooks/useEDITransactionCodes'
import { EdiSchemaType } from '~/protos/agents'
import { SchemaTypeProps } from './types'

export interface EdiSchemaData {
  ediTransactionVersion: string
  ediTransactionType: string
}

export function Edi({ sourceDataType, samples, onChange, onSchemaChange }: SchemaTypeProps) {
  const { data: ediTransactionCodes } = useEDITransactionCodes()
  const [ediTransactionVersion, setEdiTransactionVersion] = useState<string>('004010')
  const [ediTransactionType, setEdiTransactionType] = useState<string>('850')

  useEffect(() => {
    if (sourceDataType === 'DATA_TYPE_EDI') {
      onSchemaChange({
        ediTransactionVersion,
        ediTransactionType
      })
    }
  }, [ediTransactionVersion, ediTransactionType, sourceDataType, onSchemaChange])

  // Create arrays of EDI version and transaction type options for select dropdowns
  const ediVersionOptions = ediTransactionCodes
    ? Object.keys(ediTransactionCodes)
        .sort((v1, v2) => Number(v1) - Number(v2)) // Sort in ascending order
        .map(version => ({
          label: version,
          key: version
        }))
    : []

  const ediTypeOptions =
    ediTransactionCodes && ediTransactionVersion
      ? (ediTransactionCodes[ediTransactionVersion] || [])
          .sort((t1, t2) => Number(t1) - Number(t2)) // Sort in ascending order
          .map(type => ({
            label: type,
            key: type
          }))
      : []

  if (sourceDataType !== 'DATA_TYPE_EDI') {
    return null
  }

  return (
    <div className='edi-selection-container mt-4 grid grid-cols-2 gap-4'>
      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          EDI Transaction Version
        </div>
        <CurieSelect
          items={ediVersionOptions}
          placeholder='Select EDI version'
          selectedKeys={[ediTransactionVersion]}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const version = selectedKeys.values().next().value as string
            setEdiTransactionVersion(version)
            // Reset transaction type when version changes
            if (ediTransactionCodes && ediTransactionCodes[version]?.length > 0) {
              setEdiTransactionType(ediTransactionCodes[version][0])
            }
          }}
        />
      </div>
      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          EDI Transaction Type
        </div>
        <CurieSelect
          items={ediTypeOptions}
          placeholder='Select EDI transaction type'
          selectedKeys={[ediTransactionType]}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const type = selectedKeys.values().next().value as string
            setEdiTransactionType(type)
          }}
        />
      </div>
    </div>
  )
}

export function createEdiSchema(schemaData: EdiSchemaData) {
  return EdiSchemaType.fromJSON({
    transactionVersion: schemaData.ediTransactionVersion,
    transactionType: schemaData.ediTransactionType
  })
}
